import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { useSubscription } from '@/contexts/useSubscription';
import HomeHeader from '@/components/home/HomeHeader';
import InterviewCountdown from '@/components/home/InterviewCountdown';
import ActionCard from '@/components/home/ActionCard';
import FreeTrialBanner from '@/components/home/FreeTrialBanner';
import PlanBadge from '@/components/home/PlanBadge';
import PremiumWelcomeBanner from '@/components/home/PremiumWelcomeBanner';

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = { opacity: 0, y: 16 };
const fadeIn = { opacity: 1, y: 0 };

const MAX_FREE_ATTEMPTS = 5;

export default function HomePage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const {
    isPremium,
    isLoading: subLoading,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    cancelAt,
  } = useSubscription();

  const [justUpgraded, setJustUpgraded] = useState(
    () => sessionStorage.getItem('citizenly_just_upgraded') === '1'
  );

  useEffect(() => {
    if (justUpgraded) {
      sessionStorage.removeItem('citizenly_just_upgraded');
    }
    // Consume the flag once on mount; subsequent dismiss is local-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showWelcome = justUpgraded && (isPremium || subLoading);

  const attemptsUsed = profile?.free_attempts_used ?? 0;
  const attemptsExhausted =
    !subLoading && !isPremium && attemptsUsed >= MAX_FREE_ATTEMPTS;

  const handleStartInterview = () => {
    if (attemptsExhausted) {
      navigate('/pricing');
      return;
    }
    navigate('/interview/intro');
  };

  const primaryCard = attemptsExhausted ? (
    <ActionCard
      title="Upgrade to Continue"
      subtitle="You've used your 5 free mock interviews. Go Premium for unlimited practice with the AI USCIS officer."
      iconName="sparkles"
      variant="primary"
      size="hero"
      ctaLabel="See plans"
      onPress={() => navigate('/pricing')}
    />
  ) : (
    <ActionCard
      title="Start Mock Interview"
      subtitle="A full 20-question session with an AI USCIS officer. Voice or text."
      iconName="mic"
      variant="primary"
      size="hero"
      onPress={handleStartInterview}
    />
  );

  const primaryCardMobile = attemptsExhausted ? (
    <ActionCard
      title="Upgrade to Continue"
      subtitle="Go Premium for unlimited interviews"
      iconName="sparkles"
      variant="primary"
      onPress={() => navigate('/pricing')}
    />
  ) : (
    <ActionCard
      title="Start Mock Interview"
      subtitle="Practice with an AI officer"
      iconName="mic"
      variant="primary"
      onPress={handleStartInterview}
    />
  );

  const planCard = subLoading ? (
    <PlanCardSkeleton />
  ) : isPremium ? (
    <PlanBadge
      currentPeriodEnd={currentPeriodEnd}
      cancelAtPeriodEnd={cancelAtPeriodEnd}
      cancelAt={cancelAt}
      pulse={justUpgraded}
    />
  ) : (
    <FreeTrialBanner
      attemptsUsed={attemptsUsed}
      maxAttempts={MAX_FREE_ATTEMPTS}
    />
  );

  return (
    <div className="relative min-h-screen bg-cream md:bg-gradient-to-br md:from-cream md:via-cream md:to-cream-warm overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[700px] hidden md:block"
        style={{
          background:
            'radial-gradient(ellipse 900px 500px at 20% 10%, rgba(212,163,115,0.18), transparent 60%)',
        }}
      />
      <div className="relative mx-auto w-full max-w-7xl px-6 py-8 md:px-8 md:py-12 lg:px-12 lg:py-16">
        <HomeHeader userName={profile?.full_name ?? null} />

        <AnimatePresence>
          {showWelcome && (
            <PremiumWelcomeBanner
              onDismiss={() => setJustUpgraded(false)}
              className="mb-4 md:mb-6"
            />
          )}
        </AnimatePresence>

        {/* Mobile: stacked layout (preserves baseline) */}
        <div className="space-y-4 md:hidden">
          <InterviewCountdown interviewDate={profile?.interview_date ?? null} />
          {primaryCardMobile}
          <ActionCard
            title="Practice Questions"
            subtitle="Study 128 civics flashcards"
            iconName="book"
            variant="secondary"
            onPress={() => navigate('/practice')}
          />
          {planCard}
        </div>

        {/* Desktop: editorial 12-col grid */}
        <div className="hidden md:grid md:grid-cols-12 gap-6 lg:gap-8">
          <motion.div
            className="md:col-span-8"
            initial={fadeUp}
            animate={fadeIn}
            transition={{ duration: 0.5, delay: 0.22, ease }}
          >
            {primaryCard}
          </motion.div>

          <motion.div
            className="md:col-span-4 space-y-6"
            initial={fadeUp}
            animate={fadeIn}
            transition={{ duration: 0.5, delay: 0.12, ease }}
          >
            <InterviewCountdown interviewDate={profile?.interview_date ?? null} />
            {planCard}
          </motion.div>

          <motion.div
            className="md:col-span-4"
            initial={fadeUp}
            animate={fadeIn}
            transition={{ duration: 0.5, delay: 0.34, ease }}
          >
            <ActionCard
              title="Practice Questions"
              subtitle="Study 128 civics flashcards"
              iconName="book"
              variant="secondary"
              onPress={() => navigate('/practice')}
            />
          </motion.div>

          <motion.div
            className="md:col-span-8 rounded-2xl border-2 border-dashed border-border bg-card/50 p-6 flex items-center"
            initial={fadeUp}
            animate={fadeIn}
            transition={{ duration: 0.5, delay: 0.34, ease }}
          >
            <div>
              <p className="font-sans text-xs uppercase tracking-widest text-text-muted mb-2">
                Tip
              </p>
              <p className="font-sans text-text-secondary leading-relaxed">
                Aim for short, confident answers. Officers prefer one clear
                sentence over a paragraph — your mock interview grades the
                same way.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function PlanCardSkeleton() {
  return (
    <div className="bg-card rounded-2xl p-5 border border-border animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="h-3 w-16 bg-border rounded" />
        <div className="h-3 w-32 bg-border rounded" />
      </div>
      <div className="w-full h-2 bg-border rounded-full" />
      <div className="h-3 w-40 bg-border rounded mt-3" />
    </div>
  );
}
